import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { InstallButton } from "./InstallButton";

const links = [
  { label: "Featured", href: "#featured" },
  { label: "Genres", href: "#genres" },
  { label: "The Vault", href: "#pricing" },
];

export const Nav = ({ onEnter, onArchive }) => {
  const { user, login, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="main-nav"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1], delay: 0.3 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2 group"
        >
          <span className="h-2 w-2 rounded-full bg-blood animate-flicker" />
          <span className="font-mono text-xs md:text-sm tracking-[0.35em] uppercase text-bone">
            Old&nbsp;Geezer
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => go(l.href)}
              className="font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground hover:text-bone transition-colors hover-underline pb-1"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-link-archive"
            onClick={onArchive}
            className="font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground hover:text-bone transition-colors hover-underline pb-1"
          >
            Archive
          </button>
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <InstallButton />
          {user ? (
            <button
              data-testid="nav-logout-btn"
              onClick={logout}
              className="font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground hover:text-bone transition-colors"
            >
              Sign out
            </button>
          ) : (
            <button
              data-testid="nav-signin-btn"
              onClick={login}
              className="font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground hover:text-bone transition-colors"
            >
              Sign in
            </button>
          )}
          <button
            data-testid="nav-enter-btn"
            onClick={onEnter}
            className="inline-flex items-center gap-2 border border-white/20 hover:border-blood hover:bg-blood px-5 py-2.5 font-mono text-[11px] tracking-[0.25em] uppercase transition-all duration-300"
          >
            Enter the Vault
          </button>
        </div>

        <div className="md:hidden flex items-center gap-3">
          <InstallButton variant="compact" label="Install" />
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="font-mono text-xs tracking-[0.2em] uppercase text-bone"
          >
            {open ? "Close" : "Menu"}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 border-b border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left font-mono text-sm tracking-[0.2em] uppercase text-bone"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  onArchive();
                }}
                className="text-left font-mono text-sm tracking-[0.2em] uppercase text-bone"
              >
                Archive
              </button>
              {user ? (
                <button
                  onClick={() => {
                    setOpen(false);
                    logout();
                  }}
                  className="text-left font-mono text-sm tracking-[0.2em] uppercase text-muted-foreground"
                >
                  Sign out
                </button>
              ) : (
                <button
                  onClick={() => {
                    setOpen(false);
                    login();
                  }}
                  className="text-left font-mono text-sm tracking-[0.2em] uppercase text-bone"
                >
                  Sign in
                </button>
              )}
              <button
                onClick={() => {
                  setOpen(false);
                  onEnter();
                }}
                className="mt-2 bg-blood px-5 py-3 font-mono text-xs tracking-[0.2em] uppercase text-white"
              >
                Enter the Vault
              </button>
              <InstallButton variant="solid" className="mt-1 w-full justify-center" onDone={() => setOpen(false)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
